/* eslint-disable import/no-unresolved */
import {
  KlevuConfig,
  KlevuFetch,
  KlevuEvents,
  search,
  suggestions,
  searchCategory,
} from 'https://cdn.skypack.dev/-/@klevu/core@v5.2.3-HNRf1HrJ9AqvnoHcX5mg/dist=es2019,mode=imports,min/optimized/@klevu/core.js'
const { debounce } = window.BAO.Utils

KlevuConfig.init({
  apiKey: `${window.theme.klevu.apiKey}`,
  url: `https://${window.theme.klevu.url}/cs/v2/search`,
})

class PredictiveSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.onInputChange = debounce(this.onInputChange.bind(this), 300)
    this.listeners.add(this.els.input.element, 'input', this.onInputChange)

    this.handleDrawerTrigger = this.handleDrawerTrigger.bind(this)
    this.externalEls.trigger.elements.forEach((triggerEl) => {
      this.listeners.add(triggerEl, 'click', this.handleDrawerTrigger)
    })

    this.handleClose = this.handleClose.bind(this)
    this.listeners.add(document, 'click', this.handleClose)
  }

  handleDrawerTrigger () {
    // Align temp input element approximately where the input element is
    // so the cursor doesn't jump around
    const tempInputEl = document.createElement('input')
    tempInputEl.style.position = 'absolute'
    tempInputEl.style.top = `${this.els.input.element.offsetTop + 7}px`
    tempInputEl.style.left = `${this.els.input.element.offsetLeft}px`
    tempInputEl.style.height = 0
    tempInputEl.style.opacity = 0
    // Put this temp element as a child of the page <body> and focus on it
    document.body.appendChild(tempInputEl)
    tempInputEl.focus()

    // The keyboard is open. Now do a delayed focus on the target element
    window.setTimeout(() => {
      this.els.input.element.focus()
      this.els.input.element.click()
      // Remove the temp element
      document.body.removeChild(tempInputEl)
    }, 500)

    const searchTerm = this.els.input.element.value.trim()

    if (searchTerm.length) {
      this.showResults(true)

      this.getSearchResults(searchTerm)
    } else {
      this.showResults(false)
    }
  }

  onInputChange () {
    const searchTerm = this.els.input.element.value.trim()

    if (searchTerm.length < 2) {
      this.showResults(false)
    } else {
      this.getSearchResults(searchTerm)

      this.showResults(true)
    }
  }

  showResults (state) {
    this.externalEls['products-panel'].element.setAttribute(
      'aria-hidden',
      state === false,
    )

    this.els.queries.element.setAttribute('aria-hidden', state === false)
    this.els.products.element.setAttribute('aria-hidden', state === false)
    this.els.collections.element.setAttribute('aria-hidden', state === false)
    this.els.default.element.setAttribute('aria-hidden', state === true)
  }

  attachProductClickListeners () {
    const productEls = this.els['products-grid'].element.querySelectorAll(
      '[data-klevu-search-product]',
    )

    this.onProductClick = this.onProductClick.bind(this)

    productEls.forEach((productEl) => {
      productEl.addEventListener('click', this.onProductClick)
    })
  }

  onProductClick (event) {
    const product = event.target.closest('[data-klevu-search-product]')

    const productClickData = {
      product: {
        id: `${product.dataset.productId}`,
        name: `${product.dataset.productName}`,
        url: `${product.dataset.productUrl}`,
      },
      searchTerm: `${this.els.input.element.value.trim()}`,
    }

    KlevuEvents.searchProductClick(productClickData)
  }

  async getSearchResults (searchTerm) {
    const result = await KlevuFetch(
      suggestions(searchTerm, {
        id: 'suggestions',
      }),
      searchCategory(searchTerm, {
        id: 'category',
      }),
      search(searchTerm, {
        id: 'search',
        fields: ['url'],
        limit: 999,
      }),
    )

    const suggestionRecords =
      result.suggestionsById('suggestions')?.suggestions

    const categoryRecords = result.queriesById('category')?.records

    const records = result.queriesById('search')?.records
    const recordsTotal = result.queriesById('search').meta.totalResultsFound

    if (records !== undefined) {
      this.setupResults(records, recordsTotal, searchTerm)
    }

    this.setupSuggestions(suggestionRecords, categoryRecords)
  }

  async setupSuggestions (suggestionRecords, categoryRecords) {
    if (categoryRecords.length > 0) {
      const collectionAsideHTML = this.populationAside(
        window.theme.klevu.strings.collectionResults,
        await this.getCategoryHTML(categoryRecords),
      )
      this.els.collections.element.innerHTML = collectionAsideHTML
    }

    if (suggestionRecords.length > 0) {
      const suggestionAsideHTML = this.populationAside(
        window.theme.klevu.strings.queryResults,
        await this.getSuggestionHTML(suggestionRecords),
      )
      this.els.queries.element.innerHTML = suggestionAsideHTML
    }

    this.els.collections.element.setAttribute(
      'aria-hidden',
      categoryRecords.length === 0,
    )
    this.els.queries.element.setAttribute(
      'aria-hidden',
      suggestionRecords.length === 0,
    )
  }

  populationAside (title, items) {
    return `
      <aside class="drw-SearchAside">
        <h2 class="drw-Search_Heading fz-12_120">${title}</h2>
        <ul class="drw-SearchAside_Items">
          ${items}
        </ul>
      </aside>
    `
  }

  async getCategoryHTML (categoryRecords) {
    if (!categoryRecords || categoryRecords.length === 0) return ''

    const categoryHTML = await Promise.all(
      categoryRecords.map((category) => {
        return `
          <li class="drw-SearchAside_Item">
            <a href="${category.url}" class="drw-SearchAside_Link fz-13_150">
              ${category.name.replace(/<b>/g, '<strong class="highlight">').replace(/<\/b>/g, '</strong>')}
            </a>
          </li>
        `
      }),
    )

    return categoryHTML.filter(Boolean).join('')
  }

  async getSuggestionHTML (suggestionRecords) {
    const stripHtml = (html) => {
      return html.replace(/<\/?[^>]+(>|$)/g, '')
    }

    const suggestionHTML = await Promise.all(
      suggestionRecords.map((suggestion) => {
        const strippedSuggestion = stripHtml(suggestion.suggest)

        return `
          <li class="drw-SearchAside_Item">
            <a href="${window.Shopify.routes.root}pages/search-results?q=${strippedSuggestion}" class="drw-SearchAside_Link fz-13_150">
              ${suggestion.suggest.replace(/<b>/g, '<strong class="highlight">').replace(/<\/b>/g, '</strong>')}
            </a>
          </li>
        `
      }),
    )

    return suggestionHTML.filter(Boolean).join('')
  }

  async setupResults (records, recordsTotal, searchTerm) {
    if (records.length < 1) {
      this.externalEls['products-link'].element.setAttribute(
        'aria-hidden',
        true,
      )
      this.els['products-link'].element.setAttribute('aria-hidden', true)

      this.externalEls['products-grid'].element.innerHTML = this.onNoResults()
      this.els['products-grid'].element.innerHTML = this.onNoResults()
    } else {
      // update count
      const viewAllText = window.theme.klevu.strings.viewAll.replace(
        '{{ count }}',
        recordsTotal,
      )
      this.externalEls['products-count'].element.innerText = viewAllText
      this.els['products-count'].element.innerText = viewAllText

      // update products
      const productCardHTML = await this.getAllProductCardHTML(records)
      this.externalEls['products-grid'].element.innerHTML = productCardHTML
      this.els['products-grid'].element.innerHTML = productCardHTML

      this.attachProductClickListeners()

      // update links
      this.externalEls['products-link'].element.href =
        `${window.Shopify.routes.root}pages/search-results?q=${searchTerm}`
      this.externalEls['products-link'].element.setAttribute(
        'aria-hidden',
        false,
      )
      this.els['products-link'].element.href =
        `${window.Shopify.routes.root}pages/search-results?q=${searchTerm}`
      this.els['products-link'].element.setAttribute('aria-hidden', false)
    }
  }

  onNoResults () {
    return `
      <div class="drw-SearchProducts_Empty">
        <p class="drw-SearchProducts_EmptyTitle fz-34_120">
          ${window.theme.klevu.strings.predictiveNoResults}
        </p>

        <p class="drw-SearchProducts_EmptyText fz-13_150">
          ${window.theme.klevu.strings.predictiveNoResultsText}
        </p>
      </div>
    `
  }

  async getAllProductCardHTML (records) {
    const productCardHTML = await Promise.all(
      records.map(async (product) => {
        const productHTML = await this.getProductCardHTML(product)
        return productHTML
          ? `<li class="drw-SearchProducts_Item">${productHTML}</li>`
          : ''
      }),
    )
    return `<ul class="drw-SearchProducts_Items">${productCardHTML.filter(Boolean).join('')}</ul>`
  }

  async getProductCardHTML (product) {
    const currentMarket = window.location.origin

    const productUrl = product.url
      .replace('/products/', `${window.Shopify.routes.root}products/`)
      .replace(/^https?:\/\/[^/]+/, currentMarket)

    return window.BAO.sectionRenderer
      .fetchSingle('predictive-search-product-card', `${productUrl}`)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('article')

        if (myself) {
          return myself.outerHTML
        }
      })
      .catch(() => {
        return null
      })
  }

  handleClose (event) {
    const closeElement = event.target.closest(
      `${this.els?.close?.selector}, ${this.externalEls?.close?.selector}`,
    )

    if (!closeElement) return

    this.externalEls['products-panel'].element.setAttribute(
      'aria-hidden',
      true,
    )
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch)
}
